/**
 * Shared Settings navigation destinations for breadcrumbs, deep-links, and search.
 */

import { compactSearchMatch } from '@/lib/compact-search-match'

export type WorkspaceSectionId = 'overview' | 'extensions' | 'team' | 'keys' | 'usage' | 'danger'

export const WORKSPACE_SECTIONS: {
  id: WorkspaceSectionId
  label: string
  keywords: string[]
}[] = [
  { id: 'overview', label: 'Overview', keywords: ['overview', 'identity', 'icon', 'color', 'name'] },
  {
    id: 'extensions',
    label: 'Extensions',
    keywords: ['extensions', 'integrations', 'enable'],
  },
  { id: 'team', label: 'Team', keywords: ['team', 'members', 'invite', 'people'] },
  {
    id: 'keys',
    label: 'Keys & secrets',
    keywords: ['keys', 'secrets', 'llm', 'api key', 'anthropic', 'openai', 'custom endpoint', 'byo'],
  },
  { id: 'usage', label: 'Usage', keywords: ['usage', 'tokens', 'cost', 'spend', 'metering'] },
  { id: 'danger', label: 'Danger', keywords: ['danger', 'delete', 'remove workspace'] },
]

/** Extensions that have a dedicated settings page (Configure). */
export const EXTENSIONS_WITH_SETUP = [
  'basecamp',
  'google-drive',
  'cursor',
  'zoom',
  'smtp',
  'uptime',
  'slack',
  'nextcloud',
] as const

export type SetupExtensionId = (typeof EXTENSIONS_WITH_SETUP)[number]

export function isSetupExtensionId(id: string): id is SetupExtensionId {
  return (EXTENSIONS_WITH_SETUP as readonly string[]).includes(id)
}

export const EXTENSION_LABELS: Record<string, string> = {
  basecamp: 'Basecamp',
  'voice-memos': 'Voice Memos',
  'product-board': 'Product Board',
  cursor: 'Cursor IDE',
  'cursor-cloud': 'Cursor Cloud Agents',
  git: 'Git',
  'google-drive': 'Google Drive',
  'knowledge-hub': 'Knowledge Hub',
  quiz: 'Quiz & LMS',
  zoom: 'Zoom',
  smtp: 'SMTP',
  uptime: 'Uptime',
  slack: 'Slack',
  nextcloud: 'Nextcloud',
}

export const EXTENSION_DESCRIPTIONS: Record<string, string> = {
  basecamp: 'Connect Basecamp projects, todos, and assignments',
  'voice-memos': 'Transcribe and organize voice recordings',
  'product-board': 'NOW / NEXT / LATER roadmap from the app repo (docs/product/roadmap/)',
  cursor: 'Open Maater documents in Cursor IDE and sync edits back automatically',
  'cursor-cloud': 'Dispatch handoffs to Cursor Cloud Agents — agents implement and open MRs',
  git: 'Bind organization content paths to Git repositories (GitSource)',
  'google-drive': 'Import Google Docs, sync comments, and push edits back to Drive',
  'knowledge-hub': 'Document review lifecycle, changelog, and verification workflows',
  quiz: 'AI-generated quizzes, timed exam mode, and result tracking (no external credentials)',
  zoom: 'Auto-capture meeting transcripts via webhook and run meeting-notes flows',
  smtp: 'Tenant-owned outbound SMTP for flows (email.send / email.send_batch)',
  uptime: 'Read Uptime.com checks, outages, and alerts for outage watch',
  slack: 'Read one Slack channel, its threads, and reactions for outage watch',
  nextcloud: 'Browse a Nextcloud instance and import files into this workspace',
}

/** Search aliases → extension id */
export const EXTENSION_SEARCH_ALIASES: { extensionId: string; keywords: string[] }[] = [
  {
    extensionId: 'basecamp',
    keywords: [
      'basecamp',
      'listening',
      'webhook',
      'todo',
      'folder',
      'mapping',
      'project path',
    ],
  },
  {
    extensionId: 'google-drive',
    keywords: ['google drive', 'drive', 'gdoc', 'docs', 'publish'],
  },
  {
    extensionId: 'cursor',
    keywords: ['cursor', 'vsix', 'ide', 'code-workspace', 'pat', 'token'],
  },
  {
    extensionId: 'zoom',
    keywords: ['zoom', 'transcript', 'meeting'],
  },
  {
    extensionId: 'smtp',
    keywords: ['smtp', 'email', 'mail', 'brevo', 'outbound'],
  },
  {
    extensionId: 'uptime',
    keywords: ['uptime', 'outage', 'monitor', 'downtime', 'hop-notifications'],
  },
  {
    extensionId: 'slack',
    keywords: ['slack', 'hop-notifications', 'reaction', 'checkmark', 'channel'],
  },
  {
    extensionId: 'nextcloud',
    keywords: ['nextcloud', 'webdav', 'files', 'lightweb', 'share link'],
  },
  {
    extensionId: 'voice-memos',
    keywords: ['voice', 'memos', 'transcribe'],
  },
  {
    extensionId: 'knowledge-hub',
    keywords: ['knowledge hub', 'review', 'changelog'],
  },
  {
    extensionId: 'git',
    keywords: ['git', 'repository', 'gitsource'],
  },
  {
    extensionId: 'cursor-cloud',
    keywords: ['cloud agents', 'cursor cloud', 'dispatch'],
  },
  {
    extensionId: 'quiz',
    keywords: ['quiz', 'lms', 'exam'],
  },
  {
    extensionId: 'product-board',
    keywords: ['product board', 'roadmap', 'now next later'],
  },
]

export type SettingsSearchDestination =
  | { kind: 'tab'; tab: string; label: string }
  | { kind: 'workspace-section'; section: WorkspaceSectionId; label: string }
  | { kind: 'extension'; extensionId: string; label: string }

export type SettingsSearchHit = SettingsSearchDestination & {
  id: string
  keywords: string[]
}

/** Account-level destinations (hosted MCP lives under Account, not Basecamp). */
export const ACCOUNT_SEARCH_HITS: SettingsSearchHit[] = [
  {
    id: 'account-agents-mcp',
    kind: 'tab',
    tab: 'account',
    label: 'Agents MCP',
    keywords: ['mcp', 'agents', 'maater', 'flownotes-cloud', 'cursor mcp', 'claude', 'hosted mcp'],
  },
]

/** Static index entries (tabs filled by SettingsPanel with live labels). */
export function buildWorkspaceSearchHits(): SettingsSearchHit[] {
  const sectionHits: SettingsSearchHit[] = WORKSPACE_SECTIONS.map((s) => ({
    id: `ws-section-${s.id}`,
    kind: 'workspace-section',
    section: s.id,
    label: s.label,
    keywords: s.keywords,
  }))
  const extensionHits: SettingsSearchHit[] = EXTENSION_SEARCH_ALIASES.map((a) => ({
    id: `ws-ext-${a.extensionId}`,
    kind: 'extension',
    extensionId: a.extensionId,
    label: EXTENSION_LABELS[a.extensionId] ?? a.extensionId,
    keywords: a.keywords,
  }))
  return [...ACCOUNT_SEARCH_HITS, ...sectionHits, ...extensionHits]
}

export function filterSettingsSearchHits(
  hits: SettingsSearchHit[],
  query: string,
): SettingsSearchHit[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  return hits.filter((hit) => {
    if (compactSearchMatch(hit.label, q)) return true
    return hit.keywords.some((k) => compactSearchMatch(k, q) || compactSearchMatch(q, k))
  })
}

/**
 * Map openSettings focusSection strings to workspace nav state.
 */
export function parseFocusSection(focusSection: string | null | undefined): {
  section?: WorkspaceSectionId
  extensionId?: string
} {
  if (!focusSection) return {}
  const s = focusSection.toLowerCase()
  if (s === 'overview') return { section: 'overview' }
  if (s === 'extensions') return { section: 'extensions' }
  if (s === 'team' || s === 'members') return { section: 'team' }
  if (s === 'keys' || s === 'secrets' || s === 'keys-secrets') return { section: 'keys' }
  if (s === 'usage') return { section: 'usage' }
  if (s === 'danger' || s === 'delete') return { section: 'danger' }
  if (s === 'basecamp' || s === 'basecamp-listening' || s === 'listening') {
    return { section: 'extensions', extensionId: 'basecamp' }
  }
  if (s.startsWith('extension:')) {
    const extensionId = s.slice('extension:'.length)
    return { section: 'extensions', extensionId }
  }
  if (isSetupExtensionId(s) || EXTENSION_LABELS[s]) {
    return { section: 'extensions', extensionId: s }
  }
  return {}
}
